import { coerceMoneyFromApi } from '@shared/utils/money';
import { mapMediaAssetDto } from '@core/media/services/media-upload.service';
import { mapMediaUrlsFromDto } from '@core/media/utils/media-urls.mapper';
import {
  Product,
  ProductAttributeDefinitionRef,
  ProductAttributeDefinitionValueRef,
  ProductAttributeValueRef,
  ProductCategoryRef,
  ProductModifierGroup,
  ProductModifierOption,
  ProductModifierOptionVariantPrice,
  ProductModifierSelectionType,
  ProductMutationResult,
  ProductStructure,
  ProductSupplySource,
  ProductType,
  ProductVariant,
  ProductVariantMutationResult,
  SyncProductSupplySourcesResult,
  VariantInventoryState,
} from '@features/catalog/models/product.models';
import {
  GraphqlMutationProductVariantPayloadDto,
  GraphqlMutationProductPayloadDto,
  GraphqlMutationSyncProductSupplySourcesPayloadDto,
  GraphqlProductAttributeDefinitionRefDto,
  GraphqlProductAttributeValueRefDto,
  GraphqlProductCategoryRefDto,
  GraphqlProductDto,
  GraphqlProductModifierGroupDto,
  GraphqlProductModifierOptionDto,
  GraphqlProductModifierOptionVariantPriceDto,
  GraphqlProductSupplySourceDto,
  GraphqlProductVariantDto,
  GraphqlMediaAssetUrlsDto,
} from '../dto/product.dto';
import { normalizeGraphqlErrors } from '@features/catalog/data-access/mappers/graphql-error.mapper';

const PRODUCT_TYPES: readonly ProductType[] = ['provider', 'pos'];
const PRODUCT_STRUCTURES: readonly ProductStructure[] = ['simple', 'variable', 'grouped'];
const VARIANT_INVENTORY_STATES: readonly VariantInventoryState[] = ['off', 'active', 'incomplete'];
const PRODUCT_MODIFIER_SELECTION_TYPES: readonly ProductModifierSelectionType[] = [
  'single',
  'multiple',
  'quantity',
];

function asProductType(raw?: string | null): ProductType | null {
  if (raw == null) {
    return null;
  }
  const normalized = String(raw).trim().toLowerCase();
  if (normalized === 'provider_product') {
    return 'provider';
  }
  if (normalized === 'pos_product') {
    return 'pos';
  }
  return (PRODUCT_TYPES as readonly string[]).includes(normalized)
    ? (normalized as ProductType)
    : null;
}

function asProductStructure(raw?: string | null): ProductStructure | null {
  if (raw == null) {
    return null;
  }
  const normalized = String(raw).trim().toLowerCase();
  return (PRODUCT_STRUCTURES as readonly string[]).includes(normalized)
    ? (normalized as ProductStructure)
    : null;
}

function asVariantInventoryState(raw?: string | null): VariantInventoryState | null {
  if (raw == null) {
    return null;
  }
  const normalized = String(raw).trim().toLowerCase();
  return (VARIANT_INVENTORY_STATES as readonly string[]).includes(normalized)
    ? (normalized as VariantInventoryState)
    : null;
}

function asProductModifierSelectionType(raw?: string | null): ProductModifierSelectionType {
  const normalized = String(raw ?? '').trim().toLowerCase();
  return (PRODUCT_MODIFIER_SELECTION_TYPES as readonly string[]).includes(normalized)
    ? (normalized as ProductModifierSelectionType)
    : 'single';
}

function mapMediaUrlsDto(dto?: GraphqlMediaAssetUrlsDto | null) {
  return mapMediaUrlsFromDto(dto);
}

export function mapProductCategoryRefDto(dto: GraphqlProductCategoryRefDto): ProductCategoryRef {
  return {
    id: String(dto.id),
    name: dto.name,
    slug: dto.slug?.trim() || null,
  };
}

export function mapProductAttributeDefinitionRefDto(
  dto: GraphqlProductAttributeDefinitionRefDto
): ProductAttributeDefinitionRef {
  return {
    id: String(dto.id),
    name: dto.name,
    slug: dto.slug?.trim() || null,
    active: dto.active ?? true,
    values: (dto.productAttributeValues ?? []).map(mapProductAttributeDefinitionValueRefDto),
  };
}

export function mapProductAttributeDefinitionValueRefDto(
  dto: { id: string | number; value: string; active?: boolean | null }
): ProductAttributeDefinitionValueRef {
  return {
    id: String(dto.id),
    value: dto.value,
    active: dto.active ?? true,
  };
}

export function mapProductAttributeValueRefDto(
  dto: GraphqlProductAttributeValueRefDto
): ProductAttributeValueRef {
  return {
    id: String(dto.id),
    value: dto.value,
    productAttributeDefinition: dto.productAttributeDefinition
      ? mapProductAttributeDefinitionRefDto(dto.productAttributeDefinition)
      : { id: '', name: '', active: true, values: [] },
  };
}

export function mapProductVariantDto(dto: GraphqlProductVariantDto): ProductVariant {
  return {
    id: String(dto.id),
    productId: dto.productId != null ? String(dto.productId) : undefined,
    companyId: dto.companyId != null ? String(dto.companyId) : undefined,
    name: dto.name,
    sku: dto.sku?.trim() || null,
    externalReference: dto.externalReference?.trim() || null,
    imageUrl: dto.imageUrl?.trim() || null,
    imageUrls: mapMediaUrlsDto(dto.imageUrls),
    price: coerceMoneyFromApi(dto.price) ?? 0,
    stockQuantity: dto.stockQuantity ?? null,
    lowStockThreshold: dto.lowStockThreshold ?? null,
    inventoryManaged: dto.inventoryManaged ?? false,
    inventoryItemId: dto.inventoryItemId != null ? String(dto.inventoryItemId) : null,
    position: dto.position ?? null,
    active: dto.active ?? true,
    attributesText: dto.attributesText?.trim() || null,
    productAttributeValues: (dto.productAttributeValues ?? []).map(
      mapProductAttributeValueRefDto
    ),
    weightGrams: dto.weightGrams ?? null,
    lengthCm: dto.lengthCm ?? null,
    widthCm: dto.widthCm ?? null,
    heightCm: dto.heightCm ?? null,
  };
}

export function mapProductModifierOptionDto(
  dto: GraphqlProductModifierOptionDto
): ProductModifierOption {
  return {
    id: String(dto.id),
    definitionId:
      dto.productModifierOptionDefinitionId != null
        ? String(dto.productModifierOptionDefinitionId)
        : null,
    name: dto.name,
    description: dto.description ?? null,
    priceDelta: coerceMoneyFromApi(dto.priceDelta) ?? 0,
    variantPrices: (dto.variantPrices ?? []).map(mapProductModifierOptionVariantPriceDto),
    imageUrl: dto.imageUrl?.trim() || null,
    imageUrls: mapMediaUrlsDto(dto.imageUrls),
    maxQuantity: dto.maxQuantity ?? null,
    position: dto.position ?? 0,
    active: dto.active ?? true,
  };
}

export function mapProductModifierOptionVariantPriceDto(
  dto: GraphqlProductModifierOptionVariantPriceDto
): ProductModifierOptionVariantPrice {
  return {
    variantId: String(dto.variantId),
    variantName: dto.variantName ?? null,
    variantPosition: dto.variantPosition ?? null,
    priceDelta: coerceMoneyFromApi(dto.priceDelta) ?? 0,
  };
}

export function mapProductModifierGroupDto(
  dto: GraphqlProductModifierGroupDto
): ProductModifierGroup {
  return {
    id: String(dto.id),
    definitionId:
      dto.productModifierGroupDefinitionId != null
        ? String(dto.productModifierGroupDefinitionId)
        : null,
    name: dto.name,
    description: dto.description ?? null,
    selectionType: asProductModifierSelectionType(dto.selectionType),
    required: dto.required ?? false,
    minSelections: dto.minSelections ?? 0,
    maxSelections: dto.maxSelections ?? null,
    position: dto.position ?? 0,
    active: dto.active ?? true,
    options: (dto.options ?? []).map(mapProductModifierOptionDto),
  };
}

export function mapProductDto(dto: GraphqlProductDto): Product {
  const categories = (dto.categories ?? []).map(mapProductCategoryRefDto);
  const primaryCategory =
    categories[0] ??
    (dto.primaryCategory ? mapProductCategoryRefDto(dto.primaryCategory) : null);

  return {
    id: String(dto.id),
    name: dto.name,
    description: dto.description ?? null,
    price: coerceMoneyFromApi(dto.price) ?? 0,
    active: dto.active ?? true,
    sku: dto.sku?.trim() || null,
    imageUrl: dto.imageUrl?.trim() || null,
    imageUrls: mapMediaUrlsDto(dto.imageUrls),
    galleryImages: (dto.galleryImages ?? []).map(mapMediaAssetDto),
    maxGalleryImages: dto.maxGalleryImages ?? undefined,
    stockQuantity: dto.stockQuantity ?? null,
    lowStockThreshold: dto.lowStockThreshold ?? null,
    manageStock: dto.manageStock ?? false,
    inventoryManaged: dto.inventoryManaged ?? false,
    inventoryItemId: dto.inventoryItemId != null ? String(dto.inventoryItemId) : null,
    variantInventoryState: asVariantInventoryState(dto.variantInventoryState),
    posVisible: dto.posVisible ?? false,
    publicVisible: dto.publicVisible ?? dto.posVisible ?? false,
    catalogBestSellerEnabled: dto.catalogBestSellerEnabled ?? false,
    catalogBestSellerPosition: dto.catalogBestSellerPosition ?? null,
    catalogBadgeText: dto.catalogBadgeText?.trim() || null,
    fulfillmentLeadTimeMode: dto.fulfillmentLeadTimeMode === 'override' ? 'override' : 'inherit',
    fulfillmentLeadTimeDays: dto.fulfillmentLeadTimeDays ?? null,
    fulfillmentReadyTime: dto.fulfillmentReadyTime?.trim().slice(0, 5) || null,
    sendToKitchen: dto.sendToKitchen ?? false,
    prepRequiresMode: dto.prepRequiresMode === 'inherit' ? 'inherit' : 'override',
    prepTimeMinutes: dto.prepTimeMinutes ?? null,
    allowHalfAndHalf: dto.allowHalfAndHalf ?? false,
    productType: asProductType(dto.productType),
    productStructure: asProductStructure(dto.productStructure),
    primaryCategory,
    categories,
    productAttributeDefinitions: (dto.productAttributeDefinitions ?? []).map(
      mapProductAttributeDefinitionRefDto
    ),
    enabledAttributeValueIds: (dto.enabledProductAttributeValueIds ?? []).map((id) => String(id)),
    productVariants: dto.productVariants ? dto.productVariants.map(mapProductVariantDto) : null,
    modifierGroups: dto.modifierGroups ? dto.modifierGroups.map(mapProductModifierGroupDto) : null,
    attributeValueVariantCount: dto.attributeValueVariantCount ?? undefined,
    lockVersion: dto.lockVersion ?? undefined,
    weightGrams: dto.weightGrams ?? null,
    lengthCm: dto.lengthCm ?? null,
    widthCm: dto.widthCm ?? null,
    heightCm: dto.heightCm ?? null,
    isSupplyItem: dto.isSupplyItem ?? false,
    supplySources: (dto.supplySources ?? []).map(mapProductSupplySourceDto),
  };
}

export function mapProductSupplySourceDto(dto: GraphqlProductSupplySourceDto): ProductSupplySource {
  return {
    id: String(dto.id),
    productVariantId: dto.productVariantId != null ? String(dto.productVariantId) : null,
    providerId: String(dto.provider.id),
    providerName: dto.provider.name,
    purchasePrice: coerceMoneyFromApi(dto.supplyProduct.price) ?? 0,
    supplyProductId: String(dto.supplyProduct.id),
    supplyProductName: dto.supplyProduct.name,
  };
}

export function mapSyncProductSupplySourcesPayload(
  payload?: GraphqlMutationSyncProductSupplySourcesPayloadDto | null
): SyncProductSupplySourcesResult {
  if (!payload) {
    return { isSupplyItem: false, supplySources: [], errors: ['Respuesta vacía del servidor'] };
  }
  const errors = normalizeGraphqlErrors(payload.errors);
  return {
    isSupplyItem: payload.product?.isSupplyItem ?? false,
    supplySources: (payload.supplySources ?? []).map(mapProductSupplySourceDto),
    errors,
  };
}

export function mapMutationProductPayload(
  payload?: GraphqlMutationProductPayloadDto | null
): ProductMutationResult {
  if (!payload) {
    return { product: null, errors: ['Respuesta vacía del servidor'] };
  }
  const errors = normalizeGraphqlErrors(payload.errors);
  const product = payload.product ? mapProductDto(payload.product) : null;
  return { product, errors };
}

export function mapMutationProductVariantPayload(
  payload?: GraphqlMutationProductVariantPayloadDto | null
): ProductVariantMutationResult {
  if (!payload) {
    return { variant: null, errors: ['Respuesta vacía del servidor'] };
  }
  const errors = normalizeGraphqlErrors(payload.errors);
  const variant = payload.variant ? mapProductVariantDto(payload.variant) : null;
  return { variant, errors };
}
